/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.ui.CauceDateField
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.core.ui.scaffold.KptScaffold
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_anchor_date_label
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_fee_label
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_form_title
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_frequency_biweekly
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_frequency_custom
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_frequency_label
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_frequency_monthly
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_frequency_weekly
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_interval_days_label
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_save
import studio.oddpods.cauce.feature.sessions.generated.resources.sessions_patient_label

@Composable
private fun RecurrenceFrequency.label(): String = when (this) {
    RecurrenceFrequency.WEEKLY -> stringResource(Res.string.recurring_schedule_frequency_weekly)
    RecurrenceFrequency.BIWEEKLY -> stringResource(Res.string.recurring_schedule_frequency_biweekly)
    RecurrenceFrequency.MONTHLY -> stringResource(Res.string.recurring_schedule_frequency_monthly)
    RecurrenceFrequency.CUSTOM -> stringResource(Res.string.recurring_schedule_frequency_custom)
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateRecurringScheduleScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    preselectedPatientId: String? = null,
    viewModel: CreateRecurringScheduleViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    LaunchedEffect(preselectedPatientId, state.isLoadingPatients) {
        if (preselectedPatientId != null && !state.isLoadingPatients && state.selectedPatientId == null) {
            viewModel.trySendAction(CreateRecurringScheduleAction.PatientSelected(preselectedPatientId))
        }
    }

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        when (event) {
            CreateRecurringScheduleEvent.SaveSuccess -> onBackClick()
        }
    }

    KptScaffold(
        onNavigationIconClick = onBackClick,
        title = stringResource(Res.string.recurring_schedule_form_title),
        modifier = modifier,
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Spacer(modifier = Modifier.height(8.dp))

            var patientExpanded by remember { mutableStateOf(false) }
            val selectedPatient = state.patients.find { it.id == state.selectedPatientId }

            ExposedDropdownMenuBox(
                expanded = patientExpanded,
                onExpandedChange = { patientExpanded = it },
            ) {
                OutlinedTextField(
                    value = if (state.isLoadingPatients) "" else selectedPatient?.name ?: "",
                    onValueChange = {},
                    readOnly = true,
                    enabled = !state.isLoadingPatients,
                    label = { Text(stringResource(Res.string.sessions_patient_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = patientExpanded) },
                    modifier = Modifier.fillMaxWidth().menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                )
                ExposedDropdownMenu(
                    expanded = patientExpanded,
                    onDismissRequest = { patientExpanded = false },
                ) {
                    state.patients.forEach { patient ->
                        DropdownMenuItem(
                            text = { Text(patient.name) },
                            onClick = {
                                viewModel.trySendAction(CreateRecurringScheduleAction.PatientSelected(patient.id))
                                patientExpanded = false
                            },
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(12.dp))

            var frequencyExpanded by remember { mutableStateOf(false) }

            ExposedDropdownMenuBox(
                expanded = frequencyExpanded,
                onExpandedChange = { frequencyExpanded = it },
            ) {
                OutlinedTextField(
                    value = state.frequency.label(),
                    onValueChange = {},
                    readOnly = true,
                    label = { Text(stringResource(Res.string.recurring_schedule_frequency_label)) },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = frequencyExpanded) },
                    modifier = Modifier.fillMaxWidth().menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable),
                )
                ExposedDropdownMenu(
                    expanded = frequencyExpanded,
                    onDismissRequest = { frequencyExpanded = false },
                ) {
                    RecurrenceFrequency.entries.forEach { freq ->
                        DropdownMenuItem(
                            text = { Text(freq.label()) },
                            onClick = {
                                viewModel.trySendAction(CreateRecurringScheduleAction.FrequencyChanged(freq))
                                frequencyExpanded = false
                            },
                        )
                    }
                }
            }

            if (state.frequency == RecurrenceFrequency.CUSTOM) {
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = state.intervalDaysInput,
                    onValueChange = { viewModel.trySendAction(CreateRecurringScheduleAction.IntervalDaysChanged(it)) },
                    label = { Text(stringResource(Res.string.recurring_schedule_interval_days_label)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                )
            }

            Spacer(modifier = Modifier.height(12.dp))

            CauceDateField(
                selectedDate = state.anchorDate,
                onDateSelected = { viewModel.trySendAction(CreateRecurringScheduleAction.AnchorDateChanged(it)) },
                dateFormatter = viewModel.dateFormatter,
                label = { Text(stringResource(Res.string.recurring_schedule_anchor_date_label)) },
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.feeInput,
                onValueChange = { viewModel.trySendAction(CreateRecurringScheduleAction.FeeChanged(it)) },
                label = { Text(stringResource(Res.string.recurring_schedule_fee_label)) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )

            if (state.error != null) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(state.error!!.asString(), color = MaterialTheme.colorScheme.error)
            }

            Spacer(modifier = Modifier.height(24.dp))

            Button(
                onClick = { viewModel.trySendAction(CreateRecurringScheduleAction.SaveClicked) },
                enabled = !state.isSaving,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(stringResource(Res.string.recurring_schedule_save))
            }

            Spacer(modifier = Modifier.height(16.dp))
        }
    }
}
